import { ECallEventName } from "./enums/call-event-name.enum";
import { EClientEventName } from "./enums/client-event-name.enum";
import { ECallState } from "./enums/call-state.enum";
import { CallOptions } from "./interfaces/call-options.interface";
import {
  ITranscriptionStartedEvent,
  ITranscriptionDialogueEvent,
  ITranscriptionCompletedEvent,
} from "./interfaces/transcription.interface";
import {
  ICallRecordingStartedEvent,
  ICallRecordingCompletedEvent,
} from "./interfaces/recording.interface";
import { SimpleEventEmitter, SseStreamClient, SseMessageEvent, isJwtExpired } from "./utils";
import { FiretellClient } from "./firetell-client";
import { DEFAULT_ICE_SERVERS, API_ENDPOINTS } from "./constants";

/** Event-based Native WebSocket message shape */
export interface IWsEventMessage {
  event: string;
  data?: Record<string, unknown>;
}

/** Must receive session.connected within this window after opening the socket */
const WS_AUTH_TIMEOUT_MS = 3000;
/** App-level keep-alive; must stay well below Cloudflare's 100s idle timeout */
const WS_PING_INTERVAL_MS = 25000;
/** No inbound message for this long => connection considered dead */
const WS_IDLE_TIMEOUT_MS = 60000;
/** Total reconnect window; must be shorter than server DISCONNECT_GRACE_MS (15s) */
const WS_RECONNECT_WINDOW_MS = 14000;
const WS_RECONNECT_BASE_DELAY_MS = 500;
const WS_RECONNECT_MAX_DELAY_MS = 4000;
/** Max events queued while signaling is reconnecting */
const WS_OUTBOX_LIMIT = 50;
/** Custom close code used when the SDK drops a dead connection itself */
const WS_CLOSE_DEAD_CONNECTION = 4000;

export class Call extends SimpleEventEmitter {
  public callId: string | null = null;
  public from: string;
  public from_name: string;
  public from_avatar: string | null = null;
  public get avatar(): string | null {
    return this.from_avatar;
  }
  public to: string;
  public active: boolean = false;
  private client: FiretellClient | null;
  private ws: WebSocket | null = null;
  private callSseClient: SseStreamClient | null = null;
  private state: ECallState = ECallState.NONE;
  private peerConnection: RTCPeerConnection;
  public remoteDescription: RTCSessionDescriptionInit | null = null;
  private localStream: MediaStream | null = null;
  private remoteStream: MediaStream | null = null;
  public isVideo: boolean | MediaTrackConstraints;
  public isMuted: boolean = false;
  public isCameraOff: boolean = false;
  public isScreenSharing: boolean = false;
  private _cameraTrack: MediaStreamTrack | null = null;
  private _screenStream: MediaStream | null = null;
  public isTransfer: boolean;
  public transferReason?: string;
  public isInternal: boolean;
  private _destroying: boolean = false;
  private currentRemoteSetupRole: string | null = null;

  // ─── Signaling connection state (keep-alive / reconnect) ───
  private _wsUrl: string | null = null;
  private _callToken: string | null = null;
  private _signalingReady: boolean = false;
  private _reconnecting: boolean = false;
  private _reconnectAttempt: number = 0;
  private _reconnectStartedAt: number = 0;
  private _reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private _pingTimer: ReturnType<typeof setInterval> | null = null;
  private _lastWsMessageAt: number = 0;
  private _wsOutbox: IWsEventMessage[] = [];

  constructor(client: FiretellClient, options: CallOptions = {}) {
    super();
    if (!(client instanceof FiretellClient))
      throw new Error("Missing or invalid client instance");
    this.client = client;
    this.to = options.to || "";
    this.from = options.from || "";
    this.from_name = options.from_name || "";
    this.from_avatar = options.from_avatar || null;
    this.isVideo = options.isVideo || false;
    this.isTransfer = options.isTransfer || false;
    this.transferReason = options.transferReason;
    this.isInternal = options.isInternal || false;
    this.peerConnection = new RTCPeerConnection({
      iceServers: this.client?.iceServers?.length
        ? this.client.iceServers
        : DEFAULT_ICE_SERVERS,
    });
  }

  /**
   * Open dedicated Native WebSocket signaling connection for this call session
   * and authenticate with call_token within 3s.
   * The connection is kept alive with periodic pings and automatically
   * reconnected (resuming the same call) if it drops unexpectedly.
   */
  public async connectSignaling(wsUrl: string, callToken: string): Promise<void> {
    this._wsUrl = wsUrl;
    this._callToken = callToken;
    await this._openSignalingSocket(false);
  }

  /**
   * Helper to send JSON event message over this call's WebSocket.
   * While signaling is reconnecting, events are queued and flushed after resume.
   */
  public sendWsEvent(event: string, data?: Record<string, unknown>): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN && this._signalingReady) {
      this.ws.send(JSON.stringify({ event, data }));
      return;
    }
    if (this._reconnecting && this._wsOutbox.length < WS_OUTBOX_LIMIT) {
      this._wsOutbox.push({ event, data });
    }
  }

  /**
   * Whether the signaling WebSocket is currently reconnecting.
   */
  public get isReconnecting(): boolean {
    return this._reconnecting;
  }

  /**
   * Open a signaling socket and resolve once `session.connected` is received.
   */
  private _openSignalingSocket(isReconnect: boolean): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this._wsUrl) {
        reject(new Error("Missing signaling ws_url"));
        return;
      }

      let ws: WebSocket;
      try {
        ws = new WebSocket(this._wsUrl);
      } catch (err) {
        reject(err instanceof Error ? err : new Error(String(err)));
        return;
      }
      this.ws = ws;
      let settled = false;

      const authTimeout = setTimeout(() => {
        if (settled) return;
        settled = true;
        this._detachSocket(ws);
        reject(new Error("Call WebSocket authentication timed out after 3s"));
      }, WS_AUTH_TIMEOUT_MS);

      ws.onopen = () => {
        // session.connect is sent directly (bypasses the outbox / ready check)
        ws.send(
          JSON.stringify({
            event: "session.connect",
            data: this._buildSessionConnectData(isReconnect),
          })
        );
      };

      ws.onmessage = (event: MessageEvent) => {
        if (this.ws !== ws) return;
        this._lastWsMessageAt = Date.now();
        try {
          const parsed = JSON.parse(event.data) as IWsEventMessage;
          this._handleWsMessage(parsed, () => {
            clearTimeout(authTimeout);
            this._signalingReady = true;
            this._startWsPing();
            this._flushWsOutbox();
            if (!settled) {
              settled = true;
              resolve();
            }
          });
        } catch (err) {
          console.error("Call.connectSignaling::JSON parse error:", err);
        }
      };

      ws.onerror = (err) => {
        if (this.ws !== ws) return;
        // Only surface as call error for the initial connection; drops mid-call are handled by reconnect
        if (!isReconnect && !this._signalingReady) {
          this.emit(ECallEventName.STATE, { state: ECallState.ERROR, reason: "WebSocket error" });
        }
        if (!settled) {
          settled = true;
          clearTimeout(authTimeout);
          reject(err);
        }
      };

      ws.onclose = (ev: CloseEvent) => {
        clearTimeout(authTimeout);
        if (!settled) {
          settled = true;
          reject(new Error(`Call WebSocket closed (code=${ev.code})`));
        }
        if (this.ws !== ws) return; // superseded or intentionally detached
        this.ws = null;
        this._handleSignalingClose(ev.code);
      };
    });
  }

  /**
   * Build `session.connect` payload. On reconnect, include call_id so the server
   * resumes the existing session, and fall back to the client JWT if the call_token expired.
   */
  private _buildSessionConnectData(isReconnect: boolean): Record<string, unknown> {
    let token = this._callToken;
    if (isReconnect && token && isJwtExpired(token)) {
      const fallback = this.client?.getJwt();
      if (fallback) token = fallback;
    }
    const data: Record<string, unknown> = { call_token: token };
    if (isReconnect) {
      if (this.callId) data.call_id = this.callId;
      data.reconnect = true;
    }
    return data;
  }

  private _handleSignalingClose(code: number): void {
    this._stopWsPing();
    const wasReady = this._signalingReady;
    this._signalingReady = false;

    if (this._destroying) return;
    // A reconnect attempt failed: the attempt loop schedules the next try
    if (this._reconnecting) return;

    if (wasReady && this._shouldReconnect(code)) {
      this._beginReconnect(code);
      return;
    }

    if (this.active) {
      this.destroy(false);
    }
  }

  private _shouldReconnect(code: number): boolean {
    // 1000 / 1005: intentional close (server ended or transferred the session)
    if (code === 1000 || code === 1005) return false;
    if (this.state === ECallState.ENDED || this.state === ECallState.ERROR) return false;
    return Boolean(this._wsUrl && this._callToken && this.callId);
  }

  private _beginReconnect(code: number): void {
    this._reconnecting = true;
    this._reconnectAttempt = 0;
    this._reconnectStartedAt = Date.now();
    this._scheduleReconnectAttempt(code);
  }

  private _scheduleReconnectAttempt(code?: number): void {
    if (this._destroying) return;

    const elapsed = Date.now() - this._reconnectStartedAt;
    const remaining = WS_RECONNECT_WINDOW_MS - elapsed;
    if (remaining <= 0) {
      this._failReconnect();
      return;
    }

    const delay = Math.min(
      WS_RECONNECT_BASE_DELAY_MS * 2 ** this._reconnectAttempt,
      WS_RECONNECT_MAX_DELAY_MS,
      remaining
    );
    this._reconnectAttempt++;
    this.emit(ECallEventName.SIGNALING, {
      status: "reconnecting",
      attempt: this._reconnectAttempt,
      code,
    });

    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null;
      if (this._destroying) return;
      this._openSignalingSocket(true)
        .then(() => {
          if (this._destroying) return;
          this._reconnecting = false;
          this.emit(ECallEventName.SIGNALING, {
            status: "reconnected",
            attempt: this._reconnectAttempt,
          });
          this._reconnectAttempt = 0;
        })
        .catch(() => {
          this._scheduleReconnectAttempt();
        });
    }, delay);
  }

  private _failReconnect(): void {
    this._reconnecting = false;
    this._wsOutbox = [];
    this.emit(ECallEventName.SIGNALING, { status: "failed" });
    this.state = ECallState.ENDED;
    this.emit(ECallEventName.STATE, {
      state: ECallState.ENDED,
      reason: "Signaling connection lost",
    });
    this.destroy(false);
  }

  private _flushWsOutbox(): void {
    if (!this._wsOutbox.length) return;
    const queued = this._wsOutbox;
    this._wsOutbox = [];
    queued.forEach((msg) => this.sendWsEvent(msg.event, msg.data));
  }

  /**
   * Periodic app-level ping. Browsers do not expose protocol-level pings,
   * so this also lets the SDK detect half-open connections.
   */
  private _startWsPing(): void {
    this._stopWsPing();
    this._lastWsMessageAt = Date.now();
    this._pingTimer = setInterval(() => {
      const ws = this.ws;
      if (!ws) return;
      if (Date.now() - this._lastWsMessageAt > WS_IDLE_TIMEOUT_MS) {
        console.warn(`Call[${this.callId}] signaling idle timeout, reconnecting`);
        this._detachSocket(ws);
        this._handleSignalingClose(WS_CLOSE_DEAD_CONNECTION);
        return;
      }
      this.sendWsEvent("session.ping", {});
    }, WS_PING_INTERVAL_MS);
  }

  private _stopWsPing(): void {
    if (this._pingTimer) {
      clearInterval(this._pingTimer);
      this._pingTimer = null;
    }
  }

  /**
   * Remove handlers and close a socket without triggering close handling.
   */
  private _detachSocket(ws: WebSocket): void {
    ws.onopen = null;
    ws.onmessage = null;
    ws.onerror = null;
    ws.onclose = null;
    try {
      ws.close();
    } catch {
      // ignore
    }
    if (this.ws === ws) {
      this.ws = null;
    }
  }

  /**
   * Helper to extract a valid RTCSessionDescriptionInit from WS event data.
   * Handles object formats { type, sdp }, nested { sdp: { type, sdp } }, or raw SDP strings.
   */
  private _extractSdpInit(data: unknown): RTCSessionDescriptionInit | null {
    if (!data || typeof data !== "object") return null;
    const obj = data as Record<string, unknown>;

    if (obj.sdp && typeof obj.sdp === "object") {
      const sdpObj = obj.sdp as Record<string, unknown>;
      if (typeof sdpObj.sdp === "string") {
        return {
          type: (sdpObj.type as RTCSessionDescriptionInit["type"]) || "answer",
          sdp: sdpObj.sdp,
        };
      }
    }

    if (typeof obj.sdp === "string") {
      return {
        type: (obj.type as RTCSessionDescriptionInit["type"]) || "answer",
        sdp: obj.sdp,
      };
    }

    if (typeof obj.type === "string" && typeof obj.sdp === "string") {
      return {
        type: obj.type as RTCSessionDescriptionInit["type"],
        sdp: obj.sdp,
      };
    }

    return null;
  }

  /**
   * Process incoming WebSocket signaling messages for this call
   */
  private _handleWsMessage(msg: IWsEventMessage, onConnectSuccess: () => void): void {
    const { event, data } = msg;

    switch (event) {
      case "session.connected": {
        onConnectSuccess();
        break;
      }
      case "session.error": {
        if (this._reconnecting && !this._signalingReady) {
          // Failed reconnect attempt; retry loop handles it without surfacing a call error
          console.warn("Call.handleWsMessage::session.error during reconnect:", data);
          break;
        }
        console.error("Call.handleWsMessage::session.error:", data);
        this.emit(ECallEventName.STATE, {
          state: ECallState.ERROR,
          reason: (data?.message as string) || "Session error",
        });
        break;
      }
      case "call.offer": {
        if (data) {
          const sdpInit = this._extractSdpInit(data);
          if (sdpInit) {
            this.remoteDescription = sdpInit;
            void this.setRemoteDescription(this.remoteDescription);
          }
          if (data.from) this.from = (data.from as { number?: string })?.number || String(data.from);
          if (data.from_name) this.from_name = String(data.from_name);
          if (data.from_avatar !== undefined) this.from_avatar = (data.from_avatar as string) || null;
          if (data.is_video !== undefined) {
            this.isVideo = Boolean(data.is_video);
          } else if (sdpInit?.sdp) {
            this.isVideo = /m=video [1-9]/.test(sdpInit.sdp);
          }
          if (data.is_transfer !== undefined) this.isTransfer = Boolean(data.is_transfer);
          if (data.transfer_reason !== undefined) this.transferReason = String(data.transfer_reason);
        }
        this.state = ECallState.RINGING;
        this.emit(ECallEventName.STATE, { state: ECallState.RINGING, data });
        if (this.client) {
          this.client.events.emit(EClientEventName.CALL_OFFER, this);
        }
        break;
      }
      case "call.answered": {
        const sdpInit = this._extractSdpInit(data);
        if (sdpInit) {
          void this.setRemoteDescription(sdpInit);
        }
        this.state = ECallState.ACTIVE;
        this.active = true;
        this.emit(ECallEventName.STATE, { state: ECallState.ANSWERED, data });
        break;
      }
      case "call.held": {
        const sdpInit = this._extractSdpInit(data);
        if (sdpInit) {
          void this.setRemoteDescription(sdpInit);
        }
        this.state = ECallState.ONHOLD;
        this.emit(ECallEventName.STATE, { state: ECallState.ONHOLD, data });
        break;
      }
      case "call.unheld": {
        const sdpInit = this._extractSdpInit(data);
        if (sdpInit) {
          void this.setRemoteDescription(sdpInit);
        }
        this.state = ECallState.ACTIVE;
        this.emit(ECallEventName.STATE, { state: ECallState.ACTIVE, data });
        break;
      }
      case "call.camera": {
        this.emit(ECallEventName.CAMERA, {
          enabled: Boolean(data?.enabled),
          remote: true,
          data,
        });
        break;
      }
      case "call.screen_share": {
        this.emit(ECallEventName.SCREEN_SHARE, {
          sharing: Boolean(data?.sharing),
          remote: true,
          data,
        });
        break;
      }
      case "call.ended":
      case "call.rejected":
      case "call.canceled": {
        this.state = ECallState.ENDED;
        const reason = (data?.reason as string) || event;
        this.emit(ECallEventName.STATE, {
          state: ECallState.ENDED,
          reason,
          data,
        });
        if (this.client) {
          const clientEvent =
            event === "call.canceled"
              ? EClientEventName.CALL_CANCELED
              : EClientEventName.CALL_ENDED;
          this.client.events.emit(clientEvent, {
            call: this,
            call_id: this.callId,
            event,
            reason,
            data,
          });
        }
        this.destroy(false);
        break;
      }
      case "call.sdp": {
        const sdpInit = this._extractSdpInit(data);
        if (sdpInit) {
          void this.setRemoteDescription(sdpInit);
        }
        break;
      }
      case "call.state": {
        const sdpInit = this._extractSdpInit(data);
        if (sdpInit) {
          void this.setRemoteDescription(sdpInit);
        }
        if (data?.state) {
          const nextState = (data.state as ECallState) || ECallState.RINGING;
          this.state = nextState === ECallState.ANSWERED ? ECallState.ACTIVE : nextState;
        }
        this.emit(ECallEventName.STATE, data || {});
        break;
      }
    }
  }

  /**
   * Register a callback for all transcription events on this call (started, dialogue, completed).
   */
  public onTranscription(
    listener: (event: {
      type: "started" | "dialogue" | "completed";
      data:
        | ITranscriptionStartedEvent
        | ITranscriptionDialogueEvent
        | ITranscriptionCompletedEvent;
    }) => void
  ): () => void {
    this.on(ECallEventName.TRANSCRIPTION, listener);
    return () => this.off(ECallEventName.TRANSCRIPTION, listener);
  }

  /**
   * Register a callback for real-time speech dialogue transcription chunks.
   */
  public onDialogue(
    listener: (dialogue: ITranscriptionDialogueEvent) => void
  ): () => void {
    this.on(ECallEventName.TRANSCRIPTION_DIALOGUE, listener);
    return () => this.off(ECallEventName.TRANSCRIPTION_DIALOGUE, listener);
  }

  /**
   * Register a callback for active call recording events (started, completed).
   */
  public onRecording(
    listener: (event: {
      type: "started" | "completed";
      data: ICallRecordingStartedEvent | ICallRecordingCompletedEvent;
    }) => void
  ): () => void {
    this.on(ECallEventName.RECORDING, listener);
    return () => this.off(ECallEventName.RECORDING, listener);
  }

  /**
   * Connect dedicated per-call Server-Sent Events (SSE) stream for live transcription and telemetry.
   * Subscribes to `/stream?call_id=<callId>` using the call session token.
   */
  public connectCallEventStream(callToken?: string): void {
    if (!this.client || !this.callId) return;

    // Prevent opening multiple SSE streams for the same call session
    if (this.callSseClient) return;

    try {
      const sseUrl = `${this.client.getBaseUrl()}${API_ENDPOINTS.EVENT_STREAM}?call_id=${encodeURIComponent(
        this.callId
      )}`;
      const token = callToken || this.client.getJwt();

      this.callSseClient = new SseStreamClient({
        url: sseUrl,
        token,
        onMessage: (msg: SseMessageEvent) => {
          this._handleSseMessage(msg);
        },
        onError: (err) => {
          console.warn(`Call[${this.callId}] SSE stream warning:`, err);
        },
      });

      this.callSseClient.connect();
    } catch (err) {
      console.warn(`Call[${this.callId}] SSE connection failed:`, err);
    }
  }

  /**
   * Process incoming Server-Sent Events for this specific call session.
   */
  private _handleSseMessage(msg: SseMessageEvent): void {
    const { event, data } = msg;
    let payload = data;
    if (typeof payload === "string") {
      try {
        payload = JSON.parse(payload);
      } catch {
        // use raw string
      }
    }
    const d = (payload?.data || payload) || {};

    switch (event) {
      case "call.transcription.started":
      case "transcription.started": {
        const item = d as ITranscriptionStartedEvent;
        this.emit(ECallEventName.TRANSCRIPTION_STARTED, item);
        this.emit(ECallEventName.TRANSCRIPTION, { type: "started", data: item });
        break;
      }
      case "call.transcription.dialogue":
      case "transcription.dialogue": {
        const item = d as ITranscriptionDialogueEvent;
        this.emit(ECallEventName.TRANSCRIPTION_DIALOGUE, item);
        this.emit(ECallEventName.TRANSCRIPTION, { type: "dialogue", data: item });
        break;
      }
      case "call.transcription.completed":
      case "transcription.completed": {
        const item = d as ITranscriptionCompletedEvent;
        this.emit(ECallEventName.TRANSCRIPTION_COMPLETED, item);
        this.emit(ECallEventName.TRANSCRIPTION, { type: "completed", data: item });
        break;
      }
      case "call.recording.started":
      case "recording.started": {
        const item = d as ICallRecordingStartedEvent;
        this.emit(ECallEventName.RECORDING_STARTED, item);
        this.emit(ECallEventName.RECORDING, { type: "started", data: item });
        break;
      }
      case "call.recording.completed":
      case "recording.completed": {
        const item = d as ICallRecordingCompletedEvent;
        this.emit(ECallEventName.RECORDING_COMPLETED, item);
        this.emit(ECallEventName.RECORDING, { type: "completed", data: item });
        break;
      }
      default: {
        this.emit(event, d);
        break;
      }
    }
  }

  /**
   * Start an outbound call.
   * Initiates REST call creation, connects dedicated WS signaling,
   * gathers full ICE candidates, and sends call.offer.
   */
  public async start(): Promise<void> {
    if (!this.client) throw new Error("Client is not attached to Call");
    this.active = true;
    this.state = ECallState.INITIATED;
    try {
      await this._setupWebrtcMedia({ video: this.isVideo, audio: true });
      const offer = await this.peerConnection.createOffer();
      await this.peerConnection.setLocalDescription(offer);

      // Wait for all ICE candidates to be gathered (Full ICE, not Trickle)
      const sdp = await this._getSDPFull();
      const res = await this.client.initiateCallRest(this.to, this.from, Boolean(this.isVideo));
      this.callId = res.call_id;

      // Connect dedicated WS for this call session
      await this.connectSignaling(res.ws_url, res.call_token);

      // Connect dedicated SSE stream for this call session
      this.connectCallEventStream(res.call_token);

      // Send call.offer over WebSocket
      this.sendWsEvent("call.offer", { sdp: sdp.sdp });
      this.active = true;
    } catch (error: unknown) {
      this.active = false;
      this.state = ECallState.ERROR;
      this.destroy(false);
      throw error instanceof Error ? error : new Error(String(error));
    }
  }

  /**
   * Join an existing call session (e.g. Supervision: Listen, Whisper, Barge)
   * using a pre-generated call_token and ws_url.
   */
  public async joinSession(
    wsUrl: string,
    callToken: string,
    mode: "listen" | "whisper" | "barge" = "listen"
  ): Promise<void> {
    this.active = true;
    this.state = ECallState.INITIATED;
    try {
      const isListenMode = mode === "listen";
      // In listen (silent monitor) mode: do NOT request microphone permission / getUserMedia.
      // Use WebRTC recvonly transceiver so browser shows no active microphone indicator.
      await this._setupWebrtcMedia({
        video: Boolean(this.isVideo),
        audio: isListenMode ? false : true,
      });
      const offer = await this.peerConnection.createOffer();
      await this.peerConnection.setLocalDescription(offer);

      const sdp = await this._getSDPFull();
      await this.connectSignaling(wsUrl, callToken);

      // Connect dedicated SSE stream for this call session
      this.connectCallEventStream(callToken);

      this.sendWsEvent("call.offer", { sdp: sdp.sdp });
      this.active = true;
    } catch (error: unknown) {
      this.active = false;
      this.state = ECallState.ERROR;
      this.destroy(false);
      throw error instanceof Error ? error : new Error(String(error));
    }
  }

  /**
   * Hang up this call
   */
  public async hangup(): Promise<void> {
    if (!this.active) return;
    this.active = false;
    if (this.callId && this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.sendWsEvent("call.hangup", {});
    }
    this.destroy(false);
  }

  /**
   * Accept an incoming call.
   * Sets up WebRTC media, sets remote description, creates answer,
   * gathers full ICE, then sends call.answer over WebSocket.
   */
  public async accept(): Promise<void> {
    if (!this.callId) throw new Error("callId is missing");
    if (!this.remoteDescription) throw new Error("remoteDescription is missing");
    // Ensure isVideo is accurately detected from remote offer SDP if not explicitly set
    if (!this.isVideo && this.remoteDescription?.sdp) {
      this.isVideo = /m=video [1-9]/.test(this.remoteDescription.sdp);
    }
    try {
      await this._setupWebrtcMedia({ video: this.isVideo, audio: true });
      await this.setRemoteDescription(this.remoteDescription);
      const answer = await this.peerConnection.createAnswer();
      await this.peerConnection.setLocalDescription(answer);
      const sdp = await this._getSDPFull();

      this.sendWsEvent("call.answer", {
        sdp: sdp.sdp,
      });

      // Connect dedicated per-call SSE stream for live transcription and telemetry
      this.connectCallEventStream();

      this.active = true;
      this.state = ECallState.ANSWERED;
    } catch (error: unknown) {
      this.active = false;
      this.state = ECallState.ERROR;
      this.destroy(false);
      throw error instanceof Error ? error : new Error(String(error));
    }
  }

  /**
   * Reject an incoming call
   */
  public async reject(): Promise<void> {
    if (this.callId && this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.sendWsEvent("call.reject", {});
    }
    this.destroy(false);
  }

  /**
   * Transfer the call to another target.
   * Target can be: extension number (e.g. "100"), agent username,
   * team ID (te_...), or SIP account ID (si_...).
   * @param target Transfer target identifier
   * @param reason Optional transfer reason
   */
  public async transfer(
    target: string,
    reason?: string
  ): Promise<void> {
    if (!this.active) {
      throw new Error("Cannot transfer: call is not active");
    }
    if (!target) {
      throw new Error("Target is required for call transfer");
    }
    if (!this.callId) return;
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.sendWsEvent("call.transfer", {
        target,
        reason: reason || undefined,
      });
    }
    this.active = false;
    this._cleanupPeerConnection();
    if (this.client && this.callId) {
      this.client.activeCalls.delete(this.callId);
    }
    this.state = ECallState.ENDED;
    this.emit(ECallEventName.STATE, { state: ECallState.ENDED, reason: "Transferred" });
  }

  /**
   * Send a DTMF tone
   * @param digit Single digit: 0-9, *, #, A-D
   * @param duration Duration in ms (default: 250)
   */
  public async sendDTMF(digit: string, duration?: number): Promise<void> {
    if (!this.active) {
      throw new Error("Cannot send DTMF: call is not active");
    }
    if (!this.callId) return;
    this.sendWsEvent("call.dtmf", {
      digit,
      duration: duration || 250,
    });
  }

  /**
   * Mute the microphone.
   * Stops local audio tracks and notifies the server.
   */
  public async mute(): Promise<void> {
    if (!this.active) {
      throw new Error("Cannot mute: call is not active");
    }
    if (!this.callId) return;
    if (this.localStream) {
      this.localStream.getAudioTracks().forEach((track) => {
        track.enabled = false;
      });
    }
    this.isMuted = true;
    this.sendWsEvent("call.mute", { muted: true });
    this.emit(ECallEventName.MUTE, { muted: true });
  }

  /**
   * Unmute the microphone.
   * Resumes local audio tracks and notifies the server.
   */
  public async unmute(): Promise<void> {
    if (!this.active) {
      throw new Error("Cannot unmute: call is not active");
    }
    if (!this.callId) return;
    if (this.localStream) {
      this.localStream.getAudioTracks().forEach((track) => {
        track.enabled = true;
      });
    }
    this.isMuted = false;
    this.sendWsEvent("call.mute", { muted: false });
    this.emit(ECallEventName.MUTE, { muted: false });
  }

  /**
   * Toggle mute state
   */
  public async toggleMute(): Promise<void> {
    if (this.isMuted) {
      await this.unmute();
    } else {
      await this.mute();
    }
  }

  /**
   * Mute / disable local camera video track.
   * Remote party will receive black frames / paused stream.
   */
  public async muteVideo(): Promise<void> {
    if (!this.active) {
      throw new Error("Cannot mute video: call is not active");
    }
    if (this.localStream) {
      this.localStream.getVideoTracks().forEach((track) => {
        track.enabled = false;
      });
    }
    this.isCameraOff = true;
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.sendWsEvent("call.camera", { enabled: false });
    }
    this.emit(ECallEventName.CAMERA, { enabled: false, remote: false });
  }

  /**
   * Unmute / enable local camera video track.
   */
  public async unmuteVideo(): Promise<void> {
    if (!this.active) {
      throw new Error("Cannot unmute video: call is not active");
    }
    if (this.localStream) {
      this.localStream.getVideoTracks().forEach((track) => {
        track.enabled = true;
      });
    }
    this.isCameraOff = false;
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.sendWsEvent("call.camera", { enabled: true });
    }
    this.emit(ECallEventName.CAMERA, { enabled: true, remote: false });
  }

  /**
   * Toggle camera mute state
   */
  public async toggleCamera(): Promise<void> {
    if (this.isCameraOff) {
      await this.unmuteVideo();
    } else {
      await this.muteVideo();
    }
  }

  /**
   * Start sharing screen via navigator.mediaDevices.getDisplayMedia.
   * Replaces the active video track on RTCRtpSender without renegotiating SDP.
   */
  public async startScreenShare(): Promise<void> {
    if (!this.active) {
      throw new Error("Cannot start screen share: call is not active");
    }
    if (!this.isVideo) {
      throw new Error("Screen sharing is only available on video calls");
    }
    if (this.isScreenSharing) return;

    if (!navigator?.mediaDevices?.getDisplayMedia) {
      throw new Error("getDisplayMedia is not supported in this browser");
    }

    try {
      const screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: false,
      });
      const screenTrack = screenStream.getVideoTracks()[0];
      if (!screenTrack) {
        throw new Error("No video track found in display media stream");
      }

      const videoSender = this.peerConnection
        ?.getSenders()
        .find((s) => s.track && s.track.kind === "video");

      if (!videoSender) {
        screenStream.getTracks().forEach((t) => t.stop());
        throw new Error("No active video sender found in peer connection");
      }

      // Save original camera track
      this._cameraTrack = videoSender.track;
      this._screenStream = screenStream;

      // Replace track on RTCRtpSender seamlessly
      await videoSender.replaceTrack(screenTrack);

      // Replace video track in localStream so preview shows screen share
      if (this.localStream) {
        const oldTrack = this.localStream.getVideoTracks()[0];
        if (oldTrack) {
          this.localStream.removeTrack(oldTrack);
        }
        this.localStream.addTrack(screenTrack);
        this.emit(ECallEventName.LOCAL_STREAM, this.localStream);
      }

      this.isScreenSharing = true;

      // Native browser "Stop sharing" hook
      screenTrack.onended = () => {
        void this.stopScreenShare();
      };

      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.sendWsEvent("call.screen_share", { sharing: true });
      }
      this.emit(ECallEventName.SCREEN_SHARE, { sharing: true, remote: false });
    } catch (err: unknown) {
      if (this._screenStream) {
        this._screenStream.getTracks().forEach((t) => t.stop());
        this._screenStream = null;
      }
      this.isScreenSharing = false;
      throw err instanceof Error ? err : new Error(String(err));
    }
  }

  /**
   * Stop sharing screen and restore original camera video track.
   */
  public async stopScreenShare(): Promise<void> {
    if (!this.isScreenSharing) return;

    try {
      const videoSender = this.peerConnection
        ?.getSenders()
        .find((s) => s.track && s.track.kind === "video");

      // Stop screen stream tracks
      if (this._screenStream) {
        this._screenStream.getTracks().forEach((t) => t.stop());
        this._screenStream = null;
      }

      // Restore camera track if available
      if (videoSender && this._cameraTrack) {
        await videoSender.replaceTrack(this._cameraTrack);

        if (this.localStream) {
          const oldTrack = this.localStream.getVideoTracks()[0];
          if (oldTrack) {
            this.localStream.removeTrack(oldTrack);
          }
          this.localStream.addTrack(this._cameraTrack);
          this.emit(ECallEventName.LOCAL_STREAM, this.localStream);
        }
      }

      this.isScreenSharing = false;
      this._cameraTrack = null;

      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.sendWsEvent("call.screen_share", { sharing: false });
      }
      this.emit(ECallEventName.SCREEN_SHARE, { sharing: false, remote: false });
    } catch (err: unknown) {
      this.isScreenSharing = false;
      this._cameraTrack = null;
      throw err instanceof Error ? err : new Error(String(err));
    }
  }

  /**
   * Toggle screen sharing state
   */
  public async toggleScreenShare(): Promise<void> {
    if (this.isScreenSharing) {
      await this.stopScreenShare();
    } else {
      await this.startScreenShare();
    }
  }

  /**
   * Put the call on hold
   */
  public async onhold(): Promise<void> {
    if (!this.callId) return;
    this.peerConnection.getTransceivers().forEach((t) => {
      if (t.sender.track) {
        t.direction = "sendonly";
      }
    });

    const offer = await this.peerConnection.createOffer();
    await this.peerConnection.setLocalDescription(offer);
    const sdp = await this._getSDPFull();
    this.sendWsEvent("call.hold", { sdp: sdp.sdp });
    this.state = ECallState.ONHOLD;
    this.emit(ECallEventName.STATE, { state: ECallState.ONHOLD });
  }

  /**
   * Resume a held call
   */
  public async unhold(): Promise<void> {
    if (this.state !== ECallState.ONHOLD) {
      throw new Error("Call is not on hold");
    }
    if (!this.callId) return;
    this.peerConnection.getTransceivers().forEach((t) => {
      if (t.sender.track) {
        t.direction = "sendrecv";
      }
    });

    const offer = await this.peerConnection.createOffer();
    await this.peerConnection.setLocalDescription(offer);
    const sdp = await this._getSDPFull();
    this.sendWsEvent("call.unhold", { sdp: sdp.sdp });
    this.state = ECallState.ACTIVE;
    this.emit(ECallEventName.STATE, { state: ECallState.ACTIVE });
  }

  /**
   * Update call signaling state from server notification
   */
  public setSignalState(state: ECallState, params: Record<string, unknown>): void {
    this.state = state === ECallState.ANSWERED ? ECallState.ACTIVE : state;
    this.emit(ECallEventName.STATE, params);
  }

  /**
   * Get current call state
   */
  public get callState(): ECallState {
    return this.state;
  }

  /**
   * Whether the call is currently on hold
   */
  public get isHold(): boolean {
    return this.state === ECallState.ONHOLD;
  }

  /**
   * Destroy/cleanup this call instance.
   * Closes WebRTC PeerConnection, stops local/remote media tracks,
   * closes dedicated WebSocket connection, and clears event listeners.
   * @param sendHangup Whether to send call.hangup event to server (default: true, set to false for transfers)
   */
  public async destroy(sendHangup: boolean = true): Promise<void> {
    if (this._destroying) return;
    this._destroying = true;

    if (sendHangup && this.active && this.callId && this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.sendWsEvent("call.hangup", {});
    }
    this.active = false;

    // Stop keep-alive / reconnect machinery
    this._stopWsPing();
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }
    this._reconnecting = false;
    this._signalingReady = false;
    this._wsOutbox = [];

    // Close dedicated Call WebSocket (1000 = intentional, server cleans up immediately)
    if (this.ws) {
      const ws = this.ws;
      this.ws = null;
      try {
        ws.close(1000, "Call ended");
      } catch {
        // Ignore WebSocket close errors during cleanup
      }
    }

    // Close dedicated Call SSE Stream
    if (this.callSseClient) {
      try {
        this.callSseClient.close();
      } catch {
        // Ignore SSE close errors during cleanup
      }
      this.callSseClient = null;
    }

    if (this.client && this.callId) {
      this.client.activeCalls.delete(this.callId);
    }
    this.client = null;

    this._cleanupPeerConnection();

    if (this.state !== ECallState.ENDED && this.state !== ECallState.ERROR) {
      this.state = ECallState.ENDED;
      this.emit(ECallEventName.STATE, { state: ECallState.ENDED, reason: "Local Hangup" });
    }

    this.offAll();
  }

  /**
   * Set the remote SDP description on the peer connection
   */
  public async setRemoteDescription(
    sdp: RTCSessionDescriptionInit
  ): Promise<void> {
    try {
      if (sdp.type === "answer" && this.peerConnection.signalingState === "stable") {
        return;
      }

      let sdpText = sdp.sdp || "";

      // Extract DTLS setup role (active/passive/actpass) from SDP
      const setupMatch = sdpText.match(/a=setup:(active|passive|actpass)/);

      if (setupMatch) {
        if (!this.currentRemoteSetupRole) {
          // Record initial established DTLS role from server (must be 'active' or 'passive')
          if (setupMatch[1] === "active" || setupMatch[1] === "passive") {
            this.currentRemoteSetupRole = setupMatch[1];
          }
        } else if (
          (setupMatch[1] === "active" || setupMatch[1] === "passive") &&
          setupMatch[1] !== this.currentRemoteSetupRole
        ) {
          // Preserve established DTLS role during renegotiation (hold/unhold) to prevent 'Failed to set SSL role for the transport'
          sdpText = sdpText.replace(
            /a=setup:(active|passive|actpass)/g,
            `a=setup:${this.currentRemoteSetupRole}`
          );
        }
      }

      const finalSdp: RTCSessionDescriptionInit = {
        type: sdp.type,
        sdp: sdpText,
      };

      await this.peerConnection.setRemoteDescription(
        new RTCSessionDescription(finalSdp)
      );
    } catch (error) {
      console.error("setRemoteDescription:", error);
    }
  }

  /**
   * Setup WebRTC media (getUserMedia + peerConnection)
   */
  private async _setupWebrtcMedia(
    constraints: MediaStreamConstraints
  ): Promise<boolean> {
    try {
      // Make sure TURN credentials remain valid for the whole call
      const iceServers = (await this.client?.ensureIceServers()) ?? [];
      this._cleanupPeerConnection();
      this.peerConnection = new RTCPeerConnection({
        iceServers: iceServers.length ? iceServers : DEFAULT_ICE_SERVERS,
      });

      this.peerConnection.oniceconnectionstatechange = () => {
        this.emit(
          ECallEventName.MEDIA_STATE,
          this.peerConnection.iceConnectionState
        );
      };

      // Process remote stream
      this.peerConnection.ontrack = (event) => {
        let remoteStream = event.streams && event.streams[0];
        if (!remoteStream) {
          if (!this.remoteStream) {
            this.remoteStream = new MediaStream();
          }
          this.remoteStream.addTrack(event.track);
          remoteStream = this.remoteStream;
        } else {
          this.remoteStream = remoteStream;
        }
        this.emit(ECallEventName.REMOTE_STREAM, remoteStream);
      };

      if (constraints.audio || constraints.video) {
        this.localStream = await navigator.mediaDevices.getUserMedia(constraints);

        // Add local tracks to peer connection
        this.localStream.getTracks().forEach((track) =>
          this.peerConnection.addTrack(track, this.localStream!)
        );
        this.emit(ECallEventName.LOCAL_STREAM, this.localStream);
      } else {
        // Receive-only mode (e.g. Listen / Silent Monitor): NO microphone requested!
        this.peerConnection.addTransceiver("audio", { direction: "recvonly" });
      }

      return true;
    } catch (error: unknown) {
      throw error instanceof Error ? error : new Error(String(error));
    }
  }

  /**
   * Wait for ICE candidates to be gathered and return full SDP.
   * Firetell media servers do not support Trickle ICE.
   */
  private async _getSDPFull(): Promise<RTCSessionDescription> {
    return new Promise((resolve, reject) => {
      const pc = this.peerConnection;

      let isFinished = false;
      let timeoutTimer: ReturnType<typeof setTimeout> | null = null;
      let fallbackTimer: ReturnType<typeof setTimeout> | null = null;

      const finish = () => {
        if (isFinished) return;
        isFinished = true;
        if (pc) {
          pc.onicecandidate = null;
          pc.onicegatheringstatechange = null;
        }
        if (timeoutTimer) clearTimeout(timeoutTimer);
        if (fallbackTimer) clearTimeout(fallbackTimer);

        if (pc && pc.localDescription) {
          resolve(pc.localDescription);
        } else {
          reject(new Error("No local description after ICE gathering"));
        }
      };

      // Check if ICE gathering is already complete
      if (pc.iceGatheringState === "complete") {
        finish();
        return;
      }

      // Hard safety timeout of 6 seconds
      timeoutTimer = setTimeout(() => {
        if (pc.localDescription) {
          finish();
        } else {
          if (pc) {
            pc.onicecandidate = null;
            pc.onicegatheringstatechange = null;
          }
          reject(new Error("ICE gathering timed out after 6s"));
        }
      }, 6000);

      // Fallback: If 3 seconds pass and STUN Public IP candidate (typ srflx) or Relay (typ relay) is present
      fallbackTimer = setTimeout(() => {
        if (
          pc.localDescription &&
          (/typ srflx/.test(pc.localDescription.sdp) || /typ relay/.test(pc.localDescription.sdp))
        ) {
          finish();
        }
      }, 3000);

      pc.onicecandidate = (event) => {
        if (!event.candidate) {
          // ICE gathering complete (null candidate signals end)
          finish();
        }
      };

      pc.onicegatheringstatechange = () => {
        if (pc.iceGatheringState === "complete") {
          finish();
        }
      };
    });
  }

  /**
   * Cleanup the peer connection and streams
   */
  private _cleanupPeerConnection(): void {
    this.isMuted = false;
    this.currentRemoteSetupRole = null;
    if (this.peerConnection) {
      this.peerConnection.onicecandidate = null;
      this.peerConnection.oniceconnectionstatechange = null;
      this.peerConnection.onicegatheringstatechange = null;
      this.peerConnection.onconnectionstatechange = null;
      this.peerConnection.ontrack = null;
      this.peerConnection.close();
    }
    if (this._screenStream) {
      this._screenStream.getTracks().forEach((track) => track.stop());
      this._screenStream = null;
    }
    this._cameraTrack = null;
    this.isCameraOff = false;
    this.isScreenSharing = false;
    if (this.localStream) {
      this.localStream.getTracks().forEach((track) => track.stop());
      this.localStream = null;
      this.emit(ECallEventName.LOCAL_STREAM, null);
    }
    if (this.remoteStream) {
      this.remoteStream = null;
      this.emit(ECallEventName.REMOTE_STREAM, null);
    }
  }
}
